import React, { useState, useEffect } from "react";
import TailwindFlex from "library/components/_tailwind/flex";
import TailwindButton from "library/components/_tailwind/button";
import TailwindInput from "library/components/_tailwind/input";
import {
  getQABroadcastOverrides,
  setQABroadcastOverrides,
  clearQABroadcastOverrides,
  BroadcastHostOverrides,
} from "core/config/qa-broadcast-overrides";
import config from "core/config";

type Props = {};

const QABroadcastConfig: React.ComponentType<Props> = () => {
  const [overrides, setOverrides] = useState<BroadcastHostOverrides>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Load overrides on mount
  useEffect(() => {
    const stored = getQABroadcastOverrides();
    setOverrides(stored);
  }, []);

  const handleInputChange = (field: keyof BroadcastHostOverrides, value: string) => {
    setOverrides(prev => ({
      ...prev,
      [field]: value.trim() || undefined,
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    setQABroadcastOverrides(overrides);
    setHasChanges(false);
    // Reload the page to apply the new config
    window.location.reload();
  };

  const handleClear = () => {
    clearQABroadcastOverrides();
    setOverrides({});
    setHasChanges(false);
    // Reload the page to apply the default config
    window.location.reload();
  };

  const extractHost = (url: string): string => {
    try {
      const match = url.match(/^(\w+:\/\/)([^:/]+)(:\d+)?(.*)$/);
      return match ? match[2] : "";
    } catch {
      return "";
    }
  };

  const extractPort = (url: string): string => {
    try {
      const match = url.match(/^(\w+:\/\/)([^:/]+)(:\d+)?(.*)$/);
      return match && match[3] ? match[3] : "";
    } catch {
      return "";
    }
  };

  return (
    <TailwindFlex flexDirection='flex-col' gap='gap-4' padding={["p-4"]}>
      <TailwindFlex flexDirection='flex-col' gap='gap-2'>
        <div className='text-lg font-bold'>QA Broadcast Host Configuration</div>
        <div className='text-sm text-gray-600'>
          Configure custom broadcast hosts for testing. Only the host can be changed, ports are fixed.
          After saving, the page will reload to apply the new configuration.
        </div>
      </TailwindFlex>

      {/* Current Configuration Display */}
      <TailwindFlex flexDirection='flex-col' gap='gap-3' padding={["p-3"]} className={["bg-gray-100", "rounded"]}>
        <div className='font-semibold'>Current Configuration:</div>
        <TailwindFlex flexDirection='flex-col' gap='gap-1' className={["text-sm", "font-mono"]}>
          <div>
            <span className='text-gray-600'>RTMP:</span> {config.broadcastRtmp}
          </div>
          <div>
            <span className='text-gray-600'>RTMP VR:</span> {config.broadcastRtmpVr}
          </div>
          <div>
            <span className='text-gray-600'>WebSocket:</span> {config.broadcastWs}
          </div>
        </TailwindFlex>
      </TailwindFlex>

      {/* RTMP Host Override */}
      <TailwindFlex flexDirection='flex-col' gap='gap-2'>
        <label className='font-semibold'>
          RTMP Host Override
          <span className='text-sm text-gray-600 ml-2'>
            (Port: {extractPort(config.broadcastRtmp)})
          </span>
        </label>
        <TailwindInput
          value={overrides.rtmpHost || ""}
          onChange={(e) => handleInputChange("rtmpHost", e.target.value)}
          placeholder={`Current: ${extractHost(config.broadcastRtmp)}`}
          className={["font-mono"]}
        />
        <div className='text-xs text-gray-500'>
          Example: origins.streamray.com or test-origins.streamray.com
        </div>
      </TailwindFlex>

      {/* RTMP VR Host Override */}
      <TailwindFlex flexDirection='flex-col' gap='gap-2'>
        <label className='font-semibold'>
          RTMP VR Host Override
          <span className='text-sm text-gray-600 ml-2'>
            (Port: {extractPort(config.broadcastRtmpVr)})
          </span>
        </label>
        <TailwindInput
          value={overrides.rtmpVrHost || ""}
          onChange={(e) => handleInputChange("rtmpVrHost", e.target.value)}
          placeholder={`Current: ${extractHost(config.broadcastRtmpVr)}`}
          className={["font-mono"]}
        />
        <div className='text-xs text-gray-500'>
          Example: xr-origins.streamray.com or test-xr-origins.streamray.com
        </div>
      </TailwindFlex>

      {/* WebSocket Host Override */}
      <TailwindFlex flexDirection='flex-col' gap='gap-2'>
        <label className='font-semibold'>
          WebSocket Host Override
          <span className='text-sm text-gray-600 ml-2'>
            (Port: {extractPort(config.broadcastWs)})
          </span>
        </label>
        <TailwindInput
          value={overrides.wsHost || ""}
          onChange={(e) => handleInputChange("wsHost", e.target.value)}
          placeholder={`Current: ${extractHost(config.broadcastWs)}`}
          className={["font-mono"]}
        />
        <div className='text-xs text-gray-500'>
          Example: origins.streamray.com or test-origins.streamray.com
        </div>
      </TailwindFlex>

      {/* Action Buttons */}
      <TailwindFlex gap='gap-3' className={["mt-4"]}>
        <TailwindButton
          backgroundColor={"primary"}
          onClick={handleSave}
          disabled={!hasChanges}>
          Save & Reload
        </TailwindButton>
        <TailwindButton
          backgroundColor={"secondary"}
          onClick={handleClear}>
          Clear All Overrides & Reload
        </TailwindButton>
      </TailwindFlex>

      {hasChanges && (
        <div className='text-sm text-orange-600'>
          ⚠️ You have unsaved changes. Click "Save & Reload" to apply them.
        </div>
      )}
    </TailwindFlex>
  );
};

export default QABroadcastConfig;
